/**
 * ReportsListView.tsx
 *
 * Unified reports list view that adapts based on user role.
 */

import React, { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { authenticatedFetch } from '@/api/client';
import { startAnalysis } from '@/api/analysis';
import { Calendar, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRole } from '@/contexts/RoleContext';

type ReportStatus = 'incoming' | 'inprogress' | 'errored' | 'preqc' | 'qc-pending' | 'qc-reviewed';

interface Report {
  id: string;
  campaign_package_name: string;
  created_at: string;
  updated_at: string;
  issue_count: number;
  status: ReportStatus;
}

interface DeleteReportButtonProps {
  campaignPackageName: string;
  onDelete: () => void;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const DeleteReportButton: React.FC<DeleteReportButtonProps> = ({
  campaignPackageName,
  onDelete,
  onClick,
}) => (
  <button
    type="button"
    aria-label="Remove report"
    onClick={(event) => {
      onClick?.(event);
      if (window.confirm(`Remove package ${campaignPackageName} from dashboard?`)) {
        onDelete();
      }
    }}
    className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
    title="Remove report"
  >
    <Trash2 className="w-4 h-4" />
  </button>
);

// Helper function to get status label
const getStatusLabel = (status: ReportStatus): string => {
  switch (status) {
    case 'incoming':
      return 'Ready to be analysed';
    case 'inprogress':
      return 'Analysis in progress';
    case 'errored':
      return 'Analysis failed';
    case 'preqc':
      return 'PRE-QC';
    case 'qc-pending':
      return 'QC Pending';
    case 'qc-reviewed':
      return 'QC Reviewed';
    default:
      return status;
  }
};

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Table header component
const TableHeader = () => (
  <div className="grid grid-cols-[1fr_200px_200px_200px_40px] gap-4 px-6 py-3 border-b border-gray-200 text-sm font-medium text-gray-600">
    <div>Package</div>
    <div>Status</div>
    <div>Upload date</div>
    <div>Issues</div>
    <div></div>
  </div>
);

// Report row component for QC view (incoming reports)
interface ReportRowIncomingProps {
  report: Report;
  onRunAnalysis: (reportId: string) => void;
  onDelete?: (reportId: string) => void;
  isAnalyzing: boolean;
}

const ReportRowIncoming: React.FC<ReportRowIncomingProps> = ({
  report,
  onRunAnalysis,
  onDelete,
  isAnalyzing,
}) => {
  const isInProgress = report.status === 'inprogress';
  const isErrored = report.status === 'errored';
  const buttonDisabled = isAnalyzing || isInProgress;

  const getButtonText = () => {
    if (isInProgress) return 'Analysis in progress...';
    if (isErrored) return 'Retry analysis';
    return 'Start analysis';
  };

  return (
    <div className="grid grid-cols-[1fr_200px_200px_200px_40px] gap-4 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 items-center group">
      <div className="font-medium text-gray-500">{report.campaign_package_name}</div>
      <div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
          isInProgress ? 'bg-blue-100 text-blue-700' :
          isErrored ? 'bg-red-100 text-red-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {getStatusLabel(report.status)}
        </span>
      </div>
      <div className="flex items-center gap-2 text-gray-600">
        <Calendar className="w-4 h-4" />
        {formatDate(report.created_at)}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-600">-</span>
        <Button
          onClick={(e) => {
            e.preventDefault();
            onRunAnalysis(report.id);
          }}
          disabled={buttonDisabled}
          size="sm"
        >
          {getButtonText()}
        </Button>
      </div>
      <div className="flex items-center justify-center">
        {onDelete && (
          <DeleteReportButton
            campaignPackageName={report.campaign_package_name}
            onDelete={() => onDelete(report.id)}
          />
        )}
      </div>
    </div>
  );
};

// Report row component for clickable reports
interface ReportRowProps {
  report: Report;
  onRunAnalysis?: (reportId: string) => void;
  onDelete?: (reportId: string) => void;
  isAnalyzing?: boolean;
  isQcView: boolean;
}

const ReportRow: React.FC<ReportRowProps> = ({ report, onRunAnalysis, onDelete, isAnalyzing, isQcView }) => {
  const issueCount = report.issue_count;
  const isPreQC = report.status === 'preqc';
  const isIncoming = report.status === 'incoming';
  const isInProgress = report.status === 'inprogress';
  const isErrored = report.status === 'errored';
  const isClickable = !isIncoming && !isInProgress && !isErrored;
  const showAnalysisButton = (isIncoming || isPreQC || isErrored) && !isQcView; // Only show in creative view for non-QC sections
  const buttonDisabled = isAnalyzing || isInProgress;

  const getButtonText = () => {
    if (isInProgress) return 'Analysis in progress...';
    if (isPreQC) return 'Rerun analysis';
    if (isErrored) return 'Retry analysis';
    return 'Start analysis';
  };

  const statusContent = (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
      report.status === 'incoming' ? 'bg-gray-100 text-gray-700' :
      isInProgress ? 'bg-blue-100 text-blue-700' :
      report.status === 'errored' ? 'bg-red-100 text-red-700' :
      report.status === 'preqc' ? 'bg-gray-100 text-gray-700' :
      report.status === 'qc-pending' ? 'bg-yellow-100 text-yellow-700' :
      'bg-green-100 text-green-700'
    }`}>
      {getStatusLabel(report.status)}
    </span>
  );

  const rowContent = (
    <>
      <div className={`font-medium ${isClickable ? 'text-gray-900 group-hover:text-wppBlue transition-colors' : 'text-gray-500'}`}>
        {report.campaign_package_name}
      </div>
      <div>
        {statusContent}
      </div>
      <div className="flex items-center gap-2 text-gray-600">
        <Calendar className="w-4 h-4" />
        {formatDate(report.created_at)}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-600">{isIncoming || isInProgress || isErrored ? '-' : `${issueCount} Issues`}</span>
        {showAnalysisButton && onRunAnalysis && (
          <Button
            onClick={(e) => {
              e.preventDefault();
              onRunAnalysis(report.id);
            }}
            disabled={buttonDisabled}
            size="sm"
          >
            {getButtonText()}
          </Button>
        )}
        {isPreQC && isQcView && onRunAnalysis && (
          <Button
            onClick={(e) => {
              e.preventDefault();
              onRunAnalysis(report.id);
            }}
            disabled={isAnalyzing}
            size="sm"
          >
            Rerun analysis
          </Button>
        )}
      </div>
      <div className="flex items-center justify-center">
        {onDelete && (
          <DeleteReportButton
            campaignPackageName={report.campaign_package_name}
            onDelete={() => onDelete(report.id)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          />
        )}
      </div>
    </>
  );

  if (isClickable) {
    return (
      <Link
        to="/reports/$reportId"
        params={{ reportId: report.id }}
        className="grid grid-cols-[1fr_200px_200px_200px_40px] gap-4 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 items-center cursor-pointer group"
      >
        {rowContent}
      </Link>
    );
  }

  // Non-clickable row for incoming reports
  return (
    <div className="grid grid-cols-[1fr_200px_200px_200px_40px] gap-4 px-6 py-4 border-b border-gray-100 items-center group">
      {rowContent}
    </div>
  );
};

export const ReportsListView = () => {
  const { effectiveRole } = useRole();
  const isQcView = effectiveRole === 'QC';
  const queryClient = useQueryClient();

  const { data: allReports, isLoading, error } = useQuery<Report[]>({
    queryKey: ['reports'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/reports/dashboard?limit=100');
      if (!response.ok) throw new Error('Failed to fetch reports');
      return response.json();
    },
    refetchInterval: 3000, // Poll every 3 seconds (constant)
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  });

  // Mutation: start analysis
  const runAnalysisMutation = useMutation({
    mutationFn: startAnalysis,
    onMutate: async (reportId: string) => {
      await queryClient.cancelQueries({ queryKey: ['reports'] });
      const previousReports = queryClient.getQueryData<Report[]>(['reports']);

      queryClient.setQueryData<Report[]>(['reports'], (old) => {
        if (!old) return old;
        return old.map((report) =>
          report.id === reportId
            ? { ...report, status: 'inprogress' as ReportStatus }
            : report
        );
      });

      return { previousReports };
    },
    onSuccess: () => {
      // Invalidate reports query to show updated status
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (error: Error & { status?: number }, _reportId, context) => {
      if (context?.previousReports) {
        queryClient.setQueryData(['reports'], context.previousReports);
      }
      if (error.status === 409) {
        alert('Analysis is already in progress for this report. Please wait for it to complete.');
      } else {
        alert(`Failed to run analysis: ${error.message}`);
      }
    },
  });

  const handleRunAnalysis = useCallback((reportId: string) => {
    runAnalysisMutation.mutate(reportId);
  }, [runAnalysisMutation]);

  // Mutation: delete report (soft delete)
  const deleteReportMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const response = await authenticatedFetch(`/api/reports/${reportId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        let message = `Failed to delete report (HTTP ${response.status})`;

        try {
          const contentType = response.headers.get('content-type') || '';

          if (contentType.includes('application/json')) {
            const errorData = await response.json() as { detail?: string; message?: string };
            if (typeof errorData.detail === 'string' && errorData.detail.trim()) {
              message = errorData.detail;
            } else if (typeof errorData.message === 'string' && errorData.message.trim()) {
              message = errorData.message;
            }
          } else {
            const text = (await response.text()).trim();
            if (text) {
              try {
                const errorData = JSON.parse(text) as { detail?: string; message?: string };
                if (typeof errorData.detail === 'string' && errorData.detail.trim()) {
                  message = errorData.detail;
                } else if (typeof errorData.message === 'string' && errorData.message.trim()) {
                  message = errorData.message;
                } else {
                  message = text;
                }
              } catch {
                message = text;
              }
            }
          }
        } catch {
          // Keep the default status-aware fallback message if parsing fails.
        }

        const error = new Error(message) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (error: Error & { status?: number }) => {
      alert(`Failed to remove report: ${error.message}`);
    },
  });

  const handleDelete = useCallback((reportId: string) => {
    deleteReportMutation.mutate(reportId);
  }, [deleteReportMutation]);

  // Group reports by status
  const { readyToAnalyze, preQCReports, qcReports } = React.useMemo(() => {
    if (!allReports) return { readyToAnalyze: [], preQCReports: [], qcReports: [] };

    // Group reports by campaign package name (get latest version)
    const grouped = allReports.reduce((acc, report) => {
      const campaignName = report.campaign_package_name;
      if (!acc[campaignName]) {
        acc[campaignName] = report;
      } else {
        const existingDate = new Date(acc[campaignName].updated_at);
        const currentDate = new Date(report.updated_at);
        if (currentDate > existingDate) {
          acc[campaignName] = report;
        }
      }
      return acc;
    }, {} as Record<string, Report>);

    const allGroupedReports = Object.values(grouped);

    return {
      readyToAnalyze: allGroupedReports.filter(r => r.status === 'incoming' || r.status === 'inprogress' || r.status === 'errored'),
      preQCReports: allGroupedReports.filter(r => r.status === 'preqc'),
      qcReports: allGroupedReports.filter(r =>
        isQcView
          ? (r.status === 'qc-pending' || r.status === 'qc-reviewed')
          : r.status === 'qc-reviewed'
      ),
    };
  }, [allReports, isQcView]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-600">Error loading reports: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Ready to be analysed */}
      {readyToAnalyze.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Ready to be analysed
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm">
                {readyToAnalyze.length}
              </span>
            </h2>
          </div>
          <TableHeader />
          {readyToAnalyze.map((report) => (
            isQcView ? (
              <ReportRowIncoming
                key={report.id}
                report={report}
                onRunAnalysis={handleRunAnalysis}
                onDelete={handleDelete}
                isAnalyzing={runAnalysisMutation.isPending && runAnalysisMutation.variables === report.id}
              />
            ) : (
              <ReportRow
                key={report.id}
                report={report}
                onRunAnalysis={handleRunAnalysis}
                onDelete={handleDelete}
                isAnalyzing={runAnalysisMutation.isPending && runAnalysisMutation.variables === report.id}
                isQcView={isQcView}
              />
            )
          ))}
        </div>
      )}

      {/* Pre-QC Reports */}
      {preQCReports.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Pre-QC Reports
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm">
                {preQCReports.length}
              </span>
            </h2>
          </div>
          <TableHeader />
          {preQCReports.map((report) => (
            <ReportRow
              key={report.id}
              report={report}
              onRunAnalysis={handleRunAnalysis}
              onDelete={handleDelete}
              isAnalyzing={runAnalysisMutation.isPending && runAnalysisMutation.variables === report.id}
              isQcView={isQcView}
            />
          ))}
        </div>
      )}

      {/* QC Reports */}
      {qcReports.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              QC Reports
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm">
                {qcReports.length}
              </span>
            </h2>
          </div>
          <TableHeader />
          {qcReports.map((report) => (
            <ReportRow
              key={report.id}
              report={report}
              onDelete={handleDelete}
              isQcView={isQcView}
            />
          ))}
        </div>
      )}

      {/* No Reports */}
      {readyToAnalyze.length === 0 && preQCReports.length === 0 && qcReports.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-400">No reports yet</p>
        </div>
      )}
    </div>
  );
};
